import classNames from 'classnames';
import { LINK_APP_STORE } from 'constants/links';
import { useAuth } from 'context/authContext';
import { useLang } from 'context/langContext';
import { usePopup } from 'context/popupContext';
import { Namespace } from 'enums/namespaces';
import { PopupType } from 'enums/popupType';
import { Permalink, sec2time } from 'helpers';
import Analytics from 'helpers/Analytics';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { PlayBaseWithVideo } from 'typings/api';
import styles from './PlayButton.module.scss';

interface Props {
  play: PlayBaseWithVideo;
  longTitle?: boolean;
  alwaysOn?: boolean;
}

const PlayButton = ({ play, longTitle = false, alwaysOn = false }: Props): JSX.Element => {
  const { t } = useTranslation(Namespace.Common);
  const { setPopup } = usePopup();
  const { user, isUserLoaded } = useAuth();
  const { locale } = useLang();

  const isComingSoon =
    play && (!play?.video || (play.date_published_from && Date.now() < Date.parse(play.date_published_from)));
  let popupType = PopupType.BuySubscription;

  const [ios15Content, setIos15Content] = useState<JSX.Element>(null);

  const cls = classNames(
    'btn btn-primary btn-play',
    styles.play,
    !((play && isUserLoaded) || alwaysOn) ? styles.hidden : '',
    isComingSoon ? 'btn-warning btn-no-events' : '',
  );

  useEffect(() => {
    if (window.navigator.userAgent.indexOf('15_0') > -1 && isUserLoaded) {
      title = t('common:open_in_mobile_app');
      link = LINK_APP_STORE;

      setIos15Content(
        <a href={link} key="app" className={cls} tabIndex={-1} rel="external">
          {title}
        </a>,
      );
    }
  }, [isUserLoaded]);

  if (ios15Content) {
    return ios15Content;
  }

  if (isComingSoon) {
    return <span className={cls}>{t('coming_soon')}</span>;
  }

  let title: string | JSX.Element = t(longTitle ? 'play_btn_long' : 'play_btn');

  // no user -> login popup
  if (isUserLoaded && !user) {
    popupType = PopupType.Login;
  }

  // video is available
  if (play?.video?.user_progress) {
    // resume play label
    if (!longTitle) {
      title = t('continue');
    } else {
      title = (
        <>
          {t('continue')}&nbsp;
          <span>{t('continue_remaining', { time: sec2time(play?.video?.runtime - play?.video?.user_progress) })}</span>
        </>
      );
    }
  }

  let link = play ? Permalink.watch(play, locale) : null;

  if (user?.isSubscriber) {
    return (
      <a href={link} className={cls} tabIndex={-1} onMouseUp={() => (window.location.href = link)}>
        {title}
      </a>
    );
  }

  const _onClickShowPopup = (e: React.MouseEvent) => {
    e.preventDefault();
    setPopup(popupType);
  };

  return (
    <a href={link} className={cls} tabIndex={-1} onClick={_onClickShowPopup}>
      {title}
    </a>
  );
};

export default PlayButton;
